/** Presentation over an already-authorized Story evidence set. Experience
 * scenes have their own identities; they do not create observations or routes. */
export const STORY_EXPERIENCE_ACTIONS = ['explore', 'compare', 'resume'] as const;
export const STORY_EXPERIENCE_ID_PATTERN = /^[a-z][a-z0-9_-]{0,63}$/;
export const STORY_EXPERIENCE_TARGET_PATTERN = /^reg:[a-f0-9]{24}:[a-f0-9]{24}$/;
export const STORY_EXPERIENCE_SCENE_CAP = 24;
export const STORY_COLLECTION_EVIDENCE_CAP = 96;
/** How many stops one story holds, told in time or not: its reader has a rail and search to move
 *  through many. QA test report 1: a register whose rows weren't all dated stopped at 24; now every
 *  story holds 96. An authored experience still shows at most 24 scenes of its own. */
export const STORY_EVIDENCE_CAP = 96;
/** A story told in time holds the same 96. */
export const STORY_TIMELINE_EVIDENCE_CAP = STORY_EVIDENCE_CAP;
const STORY_TIMELINE_KINDS = new Set(['observation_journey', 'visual_diary']);

/** A journey or a visual diary: played in time, every row dated by approval. */
export const storyToldInTime = (kind: unknown): boolean => STORY_TIMELINE_KINDS.has(String(kind ?? ''));

export type BrainTeamStoryCollectionGroup = {
  id: string;
  label: string;
  target_ids: string[];
};

export type BrainTeamStoryCollectionBinding = {
  /** An existing evidence row identifies the register; never a new query scope. */
  target_id: string;
  subject_field_key: string;
  subject_role: 'entity' | 'category';
  stage_field_key?: string;
  observed_at_field_key?: string;
};

export type BrainTeamStoryCollection = {
  subjects: BrainTeamStoryCollectionGroup[];
  /** Authored display order, never an inferred biological or business age. */
  stages: BrainTeamStoryCollectionGroup[];
  source_bindings: BrainTeamStoryCollectionBinding[];
};

export type BrainTeamStoryExperienceLens = {
  kind: 'map' | 'collection' | 'timeline' | 'media';
  subject_ids?: string[];
  stage_ids?: string[];
  /** Collection renderer only; source meaning and authored stage order stay unchanged. */
  renderer?: '2d' | '3d';
};

export type BrainTeamStoryExperienceCamera = {
  longitude: number;
  latitude: number;
  zoom: number;
  bearing: number;
  pitch: number;
};

export type BrainTeamStoryExperienceLayer = {
  id: string;
  label: string;
  target_ids: string[];
};

export type BrainTeamStoryExperienceAction = {
  id: string;
  label: string;
} & ({ type: 'explore' | 'resume' } | { type: 'compare'; target_ids: [string, string] });

export type BrainTeamStoryExperienceScene = {
  id: string;
  title: string;
  target_id: string;
  view: {
    focus_target_ids: string[];
    visible_layer_ids: string[];
    camera?: BrainTeamStoryExperienceCamera;
    lens?: BrainTeamStoryExperienceLens;
  };
  actions: BrainTeamStoryExperienceAction[];
};

type BrainTeamStoryExperienceBase = {
  /** Directions inherit the owning playback's evidence policy. This cannot
   * request frozen facts, authorize a new source or override a release snapshot. */
  evidence_policy: 'inherit_story';
  layers: BrainTeamStoryExperienceLayer[];
  scenes: BrainTeamStoryExperienceScene[];
};

export type BrainTeamStoryExperience = BrainTeamStoryExperienceBase &
  ({ schema_version: 1; collection?: never } | { schema_version: 2; collection: BrainTeamStoryCollection });

const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const keys = (value: Record<string, unknown>, required: string[], optional: string[] = []) =>
  required.every((key) => Object.prototype.hasOwnProperty.call(value, key)) &&
  Object.keys(value).every((key) => [...required, ...optional].includes(key));

const text = (value: unknown, max: number): value is string =>
  typeof value === 'string' && value.length > 0 && value.length <= max && value === value.trim();

const id = (value: unknown): value is string =>
  typeof value === 'string' && STORY_EXPERIENCE_ID_PATTERN.test(value);

const boundedStrings = (
  value: unknown,
  max: number,
  valid: (item: unknown) => boolean,
  min = 1,
): value is string[] =>
  Array.isArray(value) &&
  value.length >= min &&
  value.length <= max &&
  new Set(value).size === value.length &&
  value.every(valid);

const target = (value: unknown): value is string =>
  typeof value === 'string' && STORY_EXPERIENCE_TARGET_PATTERN.test(value);

const finite = (value: unknown, min: number, max: number) =>
  typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;

const collectionIsValid = (
  value: unknown,
  declared: ReadonlySet<string>,
  authorized?: ReadonlySet<string>,
): value is BrainTeamStoryCollection => {
  if (
    !record(value) ||
    !keys(value, ['subjects', 'stages', 'source_bindings']) ||
    !Array.isArray(value.subjects) ||
    value.subjects.length < 1 ||
    value.subjects.length > 24 ||
    !Array.isArray(value.stages) ||
    value.stages.length > 12 ||
    !Array.isArray(value.source_bindings) ||
    value.source_bindings.length < 1 ||
    value.source_bindings.length > 8
  )
    return false;
  const groupTargets = (groups: unknown[]) => {
    const ids = new Set<string>();
    const members = new Set<string>();
    for (const group of groups) {
      if (
        !record(group) ||
        !keys(group, ['id', 'label', 'target_ids']) ||
        !id(group.id) ||
        ids.has(group.id) ||
        !text(group.label, 160) ||
        !boundedStrings(group.target_ids, STORY_COLLECTION_EVIDENCE_CAP, target) ||
        group.target_ids.some((ref) => !declared.has(ref) || members.has(ref))
      )
        return null;
      ids.add(group.id);
      group.target_ids.forEach((ref) => members.add(ref));
    }
    return members;
  };
  const subjects = groupTargets(value.subjects);
  const stages = groupTargets(value.stages);
  if (
    !subjects ||
    !stages ||
    subjects.size !== declared.size ||
    (authorized && (subjects.size !== authorized.size || [...subjects].some((ref) => !authorized.has(ref))))
  )
    return false;
  const boundRegisters = new Set<string>();
  for (const binding of value.source_bindings) {
    if (
      !record(binding) ||
      !keys(
        binding,
        ['target_id', 'subject_field_key', 'subject_role'],
        ['stage_field_key', 'observed_at_field_key'],
      ) ||
      !target(binding.target_id) ||
      !subjects.has(binding.target_id) ||
      !text(binding.subject_field_key, 96) ||
      (binding.subject_role !== 'entity' && binding.subject_role !== 'category') ||
      ('stage_field_key' in binding && !text(binding.stage_field_key, 96)) ||
      ('observed_at_field_key' in binding && !text(binding.observed_at_field_key, 96))
    )
      return false;
    const registerId = binding.target_id.split(':')[1];
    if (boundRegisters.has(registerId)) return false;
    boundRegisters.add(registerId);
  }
  return [...subjects].every((ref) => boundRegisters.has(ref.split(':')[1]));
};

const lensIsValid = (
  value: unknown,
  collection: BrainTeamStoryCollection,
): value is BrainTeamStoryExperienceLens =>
  record(value) &&
  keys(value, ['kind'], ['subject_ids', 'stage_ids', 'renderer']) &&
  ['map', 'collection', 'timeline', 'media'].includes(value.kind as string) &&
  (!('renderer' in value) ||
    (value.kind === 'collection' && ['2d', '3d'].includes(value.renderer as string))) &&
  (!('subject_ids' in value) ||
    boundedStrings(
      value.subject_ids,
      24,
      (ref) => typeof ref === 'string' && collection.subjects.some((group) => group.id === ref),
      0,
    )) &&
  (!('stage_ids' in value) ||
    boundedStrings(
      value.stage_ids,
      12,
      (ref) => typeof ref === 'string' && collection.stages.some((group) => group.id === ref),
      0,
    ));

/** Strict on request, storage and consumption boundaries, including every
 * reference nested in a view or action. Layer membership grants no authority. */
export const storyExperienceIsValid = (
  value: unknown,
  targetIds?: readonly string[],
): value is BrainTeamStoryExperience => {
  if (
    !record(value) ||
    !keys(
      value,
      value.schema_version === 2
        ? ['schema_version', 'evidence_policy', 'layers', 'scenes', 'collection']
        : ['schema_version', 'evidence_policy', 'layers', 'scenes'],
    ) ||
    (value.schema_version !== 1 && value.schema_version !== 2) ||
    value.evidence_policy !== 'inherit_story' ||
    !Array.isArray(value.layers) ||
    value.layers.length < 1 ||
    value.layers.length > 8 ||
    !Array.isArray(value.scenes) ||
    value.scenes.length < 1 ||
    value.scenes.length > STORY_EXPERIENCE_SCENE_CAP ||
    (targetIds && (new Set(targetIds).size !== targetIds.length || !targetIds.every(target)))
  ) {
    return false;
  }
  const layerIds = new Set<string>();
  const layerTargets = new Map<string, string[]>();
  const declared = new Set<string>();
  const authorized = targetIds ? new Set(targetIds) : undefined;
  for (const layer of value.layers) {
    if (
      !record(layer) ||
      !keys(layer, ['id', 'label', 'target_ids']) ||
      !id(layer.id) ||
      layerIds.has(layer.id) ||
      !text(layer.label, 80) ||
      !boundedStrings(
        layer.target_ids,
        value.schema_version === 2 ? STORY_COLLECTION_EVIDENCE_CAP : 24,
        target,
      ) ||
      layer.target_ids.some((targetId) => authorized && !authorized.has(targetId))
    ) {
      return false;
    }
    layerIds.add(layer.id);
    layerTargets.set(layer.id, layer.target_ids);
    layer.target_ids.forEach((targetId) => declared.add(targetId));
  }
  if (
    value.schema_version === 2 &&
    (declared.size > STORY_COLLECTION_EVIDENCE_CAP ||
      !collectionIsValid(value.collection, declared, authorized))
  )
    return false;
  const sceneIds = new Set<string>();
  for (const scene of value.scenes) {
    if (
      !record(scene) ||
      !keys(scene, ['id', 'title', 'target_id', 'view', 'actions']) ||
      !id(scene.id) ||
      sceneIds.has(scene.id) ||
      !text(scene.title, 160) ||
      typeof scene.target_id !== 'string' ||
      !declared.has(scene.target_id) ||
      !record(scene.view) ||
      !keys(
        scene.view,
        ['focus_target_ids', 'visible_layer_ids'],
        value.schema_version === 2 ? ['camera', 'lens'] : ['camera'],
      ) ||
      !boundedStrings(
        scene.view.focus_target_ids,
        6,
        (item) => typeof item === 'string' && declared.has(item),
      ) ||
      !scene.view.focus_target_ids.includes(scene.target_id) ||
      !boundedStrings(
        scene.view.visible_layer_ids,
        8,
        (item) => typeof item === 'string' && layerIds.has(item),
      ) ||
      !Array.isArray(scene.actions) ||
      scene.actions.length > 3
    ) {
      return false;
    }
    sceneIds.add(scene.id);
    const visible = new Set(scene.view.visible_layer_ids.flatMap((layerId) => layerTargets.get(layerId)!));
    if (scene.view.focus_target_ids.some((targetId) => !visible.has(targetId))) {
      return false;
    }
    if ('lens' in scene.view && !lensIsValid(scene.view.lens, value.collection as BrainTeamStoryCollection))
      return false;
    if (scene.view.lens) {
      const collection = value.collection as BrainTeamStoryCollection;
      const lens = scene.view.lens as BrainTeamStoryExperienceLens;
      const subjects = new Set(
        collection.subjects
          .filter((group) => !lens.subject_ids?.length || lens.subject_ids.includes(group.id))
          .flatMap((group) => group.target_ids),
      );
      const stages = lens.stage_ids?.length
        ? new Set(
            collection.stages
              .filter((group) => lens.stage_ids!.includes(group.id))
              .flatMap((group) => group.target_ids),
          )
        : null;
      if (scene.view.focus_target_ids.some((ref) => !subjects.has(ref) || (stages && !stages.has(ref))))
        return false;
    }
    if (Object.prototype.hasOwnProperty.call(scene.view, 'camera')) {
      const camera = scene.view.camera;
      if (
        !record(camera) ||
        !keys(camera, ['longitude', 'latitude', 'zoom', 'bearing', 'pitch']) ||
        !finite(camera.longitude, -180, 180) ||
        !finite(camera.latitude, -85, 85) ||
        !finite(camera.zoom, 0, 20) ||
        !finite(camera.bearing, -180, 180) ||
        !finite(camera.pitch, 0, 60)
      ) {
        return false;
      }
    }
    const actionIds = new Set<string>();
    const actionTypes = new Set<string>();
    for (const action of scene.actions) {
      if (
        !record(action) ||
        !id(action.id) ||
        actionIds.has(action.id) ||
        !text(action.label, 80) ||
        !STORY_EXPERIENCE_ACTIONS.includes(action.type as any) ||
        actionTypes.has(action.type as string) ||
        !keys(
          action,
          action.type === 'compare' ? ['id', 'label', 'type', 'target_ids'] : ['id', 'label', 'type'],
        ) ||
        (action.type === 'compare' &&
          !boundedStrings(action.target_ids, 2, (item) => typeof item === 'string' && declared.has(item), 2))
      ) {
        return false;
      }
      actionIds.add(action.id);
      actionTypes.add(action.type as string);
    }
  }
  return true;
};

/** Wider evidence is reserved for the explicit, fully valid collection grammar. */
export const storyExperienceEvidenceLimit = (value: unknown): 24 | 96 =>
  storyExperienceIsValid(value) && value.schema_version === 2 ? STORY_COLLECTION_EVIDENCE_CAP : 24;

/** How many evidence scenes one story may hold, as Asset counts them: 96, told in time or not. A live
 *  story's seed and a document's story keep their own smaller envelopes, checked where they apply. */
export const storySceneLimit = (_story?: { kind?: unknown; experience?: unknown }): number =>
  STORY_EVIDENCE_CAP;

/** Preserve authored scene order, independent of evidence ordering. */
export const parseStoryExperience = (
  value: unknown,
  targetIds?: readonly string[],
): BrainTeamStoryExperience | null =>
  storyExperienceIsValid(value, targetIds) ? remapStoryExperience(value, (targetId) => targetId) : null;

/** Public releases use this to rewrite every reference consistently with the
 * evidence snapshot. Scene/layer/action identities are presentation-only. */
export const remapStoryExperience = (
  experience: BrainTeamStoryExperience,
  targetRef: (targetId: string) => string,
  fieldRef: (fieldKey: string) => string = (fieldKey) => fieldKey,
): BrainTeamStoryExperience => ({
  ...(experience.schema_version === 2
    ? {
        schema_version: 2 as const,
        collection: {
          subjects: experience.collection.subjects.map((group) => ({
            ...group,
            target_ids: group.target_ids.map(targetRef),
          })),
          stages: experience.collection.stages.map((group) => ({
            ...group,
            target_ids: group.target_ids.map(targetRef),
          })),
          source_bindings: experience.collection.source_bindings.map((binding) => ({
            target_id: targetRef(binding.target_id),
            subject_role: binding.subject_role,
            subject_field_key: fieldRef(binding.subject_field_key),
            ...(binding.stage_field_key ? { stage_field_key: fieldRef(binding.stage_field_key) } : {}),
            ...(binding.observed_at_field_key
              ? { observed_at_field_key: fieldRef(binding.observed_at_field_key) }
              : {}),
          })),
        },
      }
    : { schema_version: 1 as const }),
  evidence_policy: 'inherit_story',
  layers: experience.layers.map((layer) => ({
    ...layer,
    target_ids: layer.target_ids.map(targetRef),
  })),
  scenes: experience.scenes.map((scene) => ({
    ...scene,
    target_id: targetRef(scene.target_id),
    view: {
      focus_target_ids: scene.view.focus_target_ids.map(targetRef),
      visible_layer_ids: [...scene.view.visible_layer_ids],
      ...(scene.view.camera ? { camera: { ...scene.view.camera } } : {}),
      ...(scene.view.lens
        ? {
            lens: {
              kind: scene.view.lens.kind,
              ...(scene.view.lens.renderer ? { renderer: scene.view.lens.renderer } : {}),
              ...(scene.view.lens.subject_ids ? { subject_ids: [...scene.view.lens.subject_ids] } : {}),
              ...(scene.view.lens.stage_ids ? { stage_ids: [...scene.view.lens.stage_ids] } : {}),
            },
          }
        : {}),
    },
    actions: scene.actions.map((action) =>
      action.type === 'compare'
        ? {
            ...action,
            target_ids: action.target_ids.map(targetRef) as [string, string],
          }
        : { ...action },
    ),
  })),
});
